/** Stored and compared as E.164: '+' plus country code and digits (no spaces). */
const STORED_E164 = /^\+[1-9]\d{7,14}$/;

/**
 * Normalize to stored E.164 (+countrycode…). Strips spaces/dashes.
 * International prefix 00… becomes +…. Does not assume a country.
 */
export function normalizeIranPhoneNumber(value: string): string {
  let compact = value.trim().replace(/[\s\-().]/g, '');
  if (!compact) return '';

  if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`;
  }

  const digits = compact.startsWith('+')
    ? compact.slice(1).replace(/\D/g, '')
    : compact.replace(/\D/g, '');

  return digits ? `+${digits}` : '';
}

/** Empty is valid (optional). Otherwise must be E.164 after normalize. */
export function isValidIranPhoneNumber(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return STORED_E164.test(normalizeIranPhoneNumber(trimmed));
}

export function isStoredE164Phone(value: string): boolean {
  return STORED_E164.test(value);
}

export function iranPhonesAreEquivalent(left: string, right: string): boolean {
  const a = left.trim() ? normalizeIranPhoneNumber(left) : '';
  const b = right.trim() ? normalizeIranPhoneNumber(right) : '';
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a === b;
}

export function getIranPhoneNumberFormatError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isValidIranPhoneNumber(trimmed)) return null;
  return 'Enter a valid phone number with country code (e.g. +14155552671 or +989123456789)';
}

/** Canonical +xx… form for inputs and API payloads; empty stays empty. */
export function toStoredE164Input(value?: string | null): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';
  return normalizeIranPhoneNumber(trimmed);
}
